import type { LabelState } from '@sunwebgroup/ui-components'
import { useIntl } from '@/intl'

import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { PricesUseCasesContext } from '@/scopes/prices/components/PricesUseCasesContext'

import { Gallery } from '@/scopes/prices/components/RoomSelector/Gallery'

import { roomSelector } from '@/scopes/prices/components/selectors'
import { createRoomSelectorInteractionEvent } from '@/scopes/prices/domain/analytics'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import {
  Button,
  Collapsible,
  CollapsibleRoot,
  CollapsibleTrigger,
  ListItem,
  RadioGroup,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'

const CollapseButton = styled(Button)`
  padding: 0;
  margin-bottom: ${tk('spacing_xs')};

  min-height: unset;

  text-decoration: underline;
  color: ${tk('color_semantic_text_strong')};
`

const CharacteristicsList = styled.ul`
  padding: 0 ${tk('spacing_md')};
  margin-block-end: 0;
  margin-block-start: 0;
`

const ListItemRoot = styled(ListItem.Root)`
  display: flex;
  flex-direction: column;
`

const ListItemHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

const ReadMoreContent = styled.div`
  width: 100%;
  padding-left: 36px;
`

interface RoomProps {
  id: string
  roomNumber: string
}

export function Room({ id, roomNumber }: RoomProps) {
  const intl = useIntl()
  const readLess = React.useMemo(() => intl.formatMessage({ id: 'prices_room_selector_read_less' }), [intl])
  const readMore = React.useMemo(() => intl.formatMessage({ id: 'prices_room_selector_read_more' }), [intl])
  const { selectRoom } = React.useContext(PricesUseCasesContext)
  const setSelectedRoom = (roomId: string | null) => {
    selectRoom(roomNumber, roomId)
  }
  const { room, stock, apiStock, isSelected } = usePricesSelector(roomSelector, roomNumber, id)
  const { name, subtitle, price, characteristics, images } = room ?? {}
  const showCollapsible = !isEmpty(characteristics) || !isEmpty(images)
  const { sendEvent } = useAnalytics()

  const [isLocalSelected, setIsLocalSelected] = React.useState(isSelected)
  const [toggleText, setToggleText] = React.useState(readMore)
  const [stockText, setStockText] = React.useState('')
  const [stockColor, setStockColor] = React.useState<LabelState | undefined>()

  const getStockText = React.useCallback((): { text: string, color: LabelState } => {
    const stockForText = isSelected ? apiStock : stock

    if (stockForText === undefined) {
      return { text: intl.formatMessage({ id: 'prices_room_selector_room-stock-on-request-room' }), color: 'info' }
    }
    if (stockForText === 1) {
      return { text: intl.formatMessage({ id: 'prices_room_selector_room-stock-last-room' }), color: 'discount' }
    }
    return {
      text: intl.formatMessage({ id: 'prices_room_selector_room-stock-last-rooms-left' }, { stock: stockForText }),
      color: 'info',
    }
  }, [isSelected, intl, apiStock, stock])

  const onChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsLocalSelected(event.currentTarget.value === id)
    setSelectedRoom(event.currentTarget.value)
  }

  React.useEffect(() => {
    setIsLocalSelected(isSelected)
  }, [isSelected])

  React.useEffect(() => {
    if (stock <= 5 || apiStock === undefined) {
      const stockObject = getStockText()
      setStockText(stockObject.text)
      setStockColor(stockObject.color)
    }
  }, [isSelected, stock, apiStock, getStockText])

  if (!room)
    return null

  const onChangeCollapsible = (open: boolean) => {
    setToggleText(open ? readLess : readMore)
    sendEvent(createRoomSelectorInteractionEvent({ type: name!, result: open ? 'Show more' : 'Show less' }))
  }

  const createAnalytics = (index: number) => {
    return createPDPClickEvent({
      element: 'Slider',
      description: 'Roomtype images',
      result: `Open image ${index + 1}`,
      type: name,
    })
  }

  return (
    <CollapsibleRoot onOpenChange={onChangeCollapsible}>
      <ListItemRoot>
        <ListItemHeader>
          <ListItem.Control>
            <RadioGroup.Item value={id} checked={isLocalSelected} onClick={onChange} />
          </ListItem.Control>
          <ListItem.Content>
            {stockText && <ListItem.Badge $state={stockColor}>{stockText}</ListItem.Badge>}
            <ListItem.Label>{name}</ListItem.Label>
            <ListItem.Description>{subtitle}</ListItem.Description>
            {showCollapsible && (
              <CollapsibleTrigger asChild>
                <CollapseButton $hierarchy="tertiary" $withoutColor>
                  {toggleText}
                </CollapseButton>
              </CollapsibleTrigger>
            )}
          </ListItem.Content>
          <ListItem.Trailing>
            <Typography $variant="label">{intl.formatCurrency(price!)}</Typography>
          </ListItem.Trailing>
        </ListItemHeader>
        {showCollapsible && (
          <ReadMoreContent>
            <Collapsible>
              <Gallery
                name={name!}
                images={images?.map((image: { src: string }) => image.src) || []}
                analyticsEventBuilder={createAnalytics}
              />
              <CharacteristicsList>
                {characteristics?.map((text: string, index: number) => (
                  <li key={index}>
                    <Typography $variant="body-sm">{text}</Typography>
                  </li>
                ))}
              </CharacteristicsList>
            </Collapsible>
          </ReadMoreContent>
        )}
      </ListItemRoot>
    </CollapsibleRoot>
  )
}
